import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';

export const pickingApi = createApi({
  reducerPath: 'pickingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.apiUrl}/picking`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.user?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Sales', 'SaleBundle'],
  endpoints: (builder) => ({
    listSales: builder.query({
      query: (params) => ({
        url: '/sales',
        params,
      }),
      providesTags: [{ type: 'Sales', id: 'LIST' }],
    }),
    getSaleBundle: builder.query({
      query: (saleId) => `/sales/${saleId}/bundle`,
      providesTags: (result, err, saleId) => [{ type: 'SaleBundle', id: saleId }],
    }),
    startOrResumePicking: builder.mutation({
      query: (saleId) => ({
        url: `/sales/${saleId}/start`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, saleId) => [
        { type: 'Sales', id: 'LIST' },
        { type: 'SaleBundle', id: saleId },
      ],
    }),
    updateLineQuantity: builder.mutation({
      query: ({ spickingId, lineId, quantity }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}`,
        method: 'PATCH',
        body: { quantity },
      }),
      invalidatesTags: (result, err, arg) => {
        const tags = [{ type: 'Sales', id: 'LIST' }];
        if (arg?.saleId) tags.push({ type: 'SaleBundle', id: arg.saleId });
        return tags;
      },
    }),
    confirmLineStock: builder.mutation({
      query: ({ spickingId, lineId, confirmed }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}/stock-confirm`,
        method: 'PATCH',
        body: { confirmed },
      }),
      invalidatesTags: (result, err, arg) => {
        const tags = [{ type: 'Sales', id: 'LIST' }];
        if (arg?.saleId) tags.push({ type: 'SaleBundle', id: arg.saleId });
        return tags;
      },
    }),
    completePicking: builder.mutation({
      query: ({ spickingId }) => ({
        url: `/spickings/${spickingId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, arg) => [
        { type: 'Sales', id: 'LIST' },
        ...(arg?.saleId ? [{ type: 'SaleBundle', id: arg.saleId }] : []),
      ],
    }),
    reopenPicking: builder.mutation({
      query: ({ spickingId }) => ({
        url: `/spickings/${spickingId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, arg) => [
        { type: 'Sales', id: 'LIST' },
        ...(arg?.saleId ? [{ type: 'SaleBundle', id: arg.saleId }] : []),
      ],
    }),
    deleteOrphanLine: builder.mutation({
      query: ({ spickingId, lineId }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, err, arg) => {
        const tags = [{ type: 'Sales', id: 'LIST' }];
        if (arg?.saleId) tags.push({ type: 'SaleBundle', id: arg.saleId });
        return tags;
      },
    }),
  }),
});

export const {
  useListSalesQuery,
  useGetSaleBundleQuery,
  useStartOrResumePickingMutation,
  useUpdateLineQuantityMutation,
  useConfirmLineStockMutation,
  useCompletePickingMutation,
  useReopenPickingMutation,
  useDeleteOrphanLineMutation,
} = pickingApi;
